import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { MeasureField, UnitToggle, type Units } from "./MeasureField";
import { NoticeBox } from "@/components/NoticeBox";

// US Navy method — a widely used tape-measurement estimate of body fat %.
function bodyFatNavy(gender: "male" | "female", heightCm: number, neckCm: number, waistCm: number, hipCm: number): number {
  if (gender === "male") {
    return 495 / (1.0324 - 0.19077 * Math.log10(waistCm - neckCm) + 0.15456 * Math.log10(heightCm)) - 450;
  }
  return 495 / (1.29579 - 0.35004 * Math.log10(waistCm + hipCm - neckCm) + 0.22100 * Math.log10(heightCm)) - 450;
}

/** Fat mass and lean mass (kg) from a body weight and a body-fat percentage. */
export function bodyComposition(weightKg: number, bfPercent: number): { fatKg: number; leanKg: number } {
  const fatKg = weightKg * (bfPercent / 100);
  return { fatKg, leanKg: weightKg - fatKg };
}

function category(bf: number, gender: "male" | "female"): string {
  const ranges = gender === "male"
    ? [[0, 6, "Essential Fat"], [6, 14, "Athletic"], [14, 18, "Fitness"], [18, 25, "Average"], [25, 100, "Above Average"]] as const
    : [[0, 14, "Essential Fat"], [14, 21, "Athletic"], [21, 25, "Fitness"], [25, 32, "Average"], [32, 100, "Above Average"]] as const;
  const found = ranges.find(([min, max]) => bf >= min && bf < max);
  return found ? found[2] : "—";
}

const TOOL_INFO_CONTENT = {
  what: `Estimates body fat percentage using body measurements (typically the U.S. Navy method, based on circumference measurements), which tends to be more informative than BMI alone for assessing body composition.`,
  how: `Enter your measurements as prompted (typically waist, neck, and for women, hip measurements), and your estimated body fat percentage is calculated.`,
  tip: `Measure at the same time of day and in the same way each time you check — small differences in where and how you measure can shift the result more than an actual change in body composition would.`,
};

export default function BodyFatCalculator() {
  const [units, setUnits] = useState<Units>("metric");
  const [gender, setGender] = useState<"male" | "female">("male");
  const [height, setHeight] = useState<number | null>(175);
  const [neck, setNeck] = useState<number | null>(38);
  const [waist, setWaist] = useState<number | null>(85);
  const [hip, setHip] = useState<number | null>(95);
  const [weight, setWeight] = useState<number | null>(null);

  const fields = height != null && neck != null && waist != null && (gender === "male" || hip != null);
  const waistVsNeck = fields && waist! <= neck!;
  const valid = fields && !waistVsNeck;
  const bf = valid ? bodyFatNavy(gender, height!, neck!, waist!, hip ?? 0) : 0;
  const sane = valid && bf > 2 && bf < 60;

  const { blocked } = useLogToolUsage(valid);

  return (
    <ToolContainer>
      <div className="max-w-md mx-auto space-y-6">
        <UnitToggle units={units} onChange={setUnits} />
        <Tabs value={gender} onValueChange={v => setGender(v as "male" | "female")}>
          <TabsList className="w-full">
            <TabsTrigger value="male" className="flex-1">Male</TabsTrigger>
            <TabsTrigger value="female" className="flex-1">Female</TabsTrigger>
          </TabsList>
        </Tabs>

        <MeasureField label="Height" kind="height" units={units} initialMetric={175} onValue={setHeight} />
        <MeasureField label="Neck" kind="length" units={units} initialMetric={38} onValue={setNeck} />
        <MeasureField label="Waist (at navel)" kind="length" units={units} initialMetric={85} onValue={setWaist} ariaLabel="Waist" />
        {gender === "female" && <MeasureField label="Hip (widest point)" kind="length" units={units} initialMetric={95} onValue={setHip} ariaLabel="Hip" />}
        <MeasureField label="Body weight (optional)" kind="weight" units={units} initialMetric={70} onValue={setWeight} ariaLabel="Body weight" optional />
        {waistVsNeck && <p className="text-sm text-red-600" data-testid="bf-error">Your waist measurement must be larger than your neck measurement. Please re-check both.</p>}

        {valid && !blocked ? (
          <div className="p-4 rounded-xl border bg-primary/5 text-center">
            <p className="text-xs text-muted-foreground">Estimated Body Fat</p>
            <p className="text-2xl font-bold text-primary" data-testid="bf-value">{bf.toFixed(1)}%</p>
            <p className="text-sm text-muted-foreground mt-1">{category(bf, gender)}</p>
            {weight != null && sane && (() => {
              const c = bodyComposition(weight, bf);
              const show = (kg: number) => units === "metric" ? `${kg.toFixed(1)} kg` : `${(kg / 0.45359237).toFixed(1)} lb`;
              return (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="p-2 rounded-lg border bg-card"><p className="text-xs text-muted-foreground">Fat mass</p><p className="text-sm font-bold" data-testid="bf-fat-mass">{show(c.fatKg)}</p></div>
                  <div className="p-2 rounded-lg border bg-card"><p className="text-xs text-muted-foreground">Lean mass</p><p className="text-sm font-bold" data-testid="bf-lean-mass">{show(c.leanKg)}</p></div>
                </div>
              );
            })()}
            {!sane && <NoticeBox className="text-xs mt-2">This result is outside the usual range. Please re-check your measurements.</NoticeBox>}
          </div>
        ) : !waistVsNeck ? (
          <p className="text-sm text-center text-muted-foreground">Enter your measurements to calculate.</p>
        ) : null}

        <p className="text-xs text-muted-foreground text-center">
          US Navy tape-measurement method: an estimate, not a substitute for clinical body composition testing (e.g. DEXA scan).
        </p>
      </div>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
